import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import {
  Breadcrumbs,
  Button,
  FullPageWrapper,
  Icon,
  type ITabItem,
  SpinnerLoader,
  Tabs,
  Tooltip,
  Typography,
} from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import { getIntegrationDetailsDataByKey } from 'entities/Integrations/lib/integrationsDescription'
import { useEntitiesContext } from 'entities/EntitiesExport'
import { AmpersandConnectButton } from 'entities/Ampersand'
import { NangoConnectButton } from 'entities/Integrations/providers/Nango'
import { IntegrationStatus } from 'widgets/IntegrationStatus'
import {
  IntegrationDetailsProvider,
  useIntegrationDetailsContext,
} from 'pages/settings/pages/integrations/context/integrationDetailsContext'
import { SettingsRoutes } from 'pages/settings/route/type'
import { IntegrationsRoutes } from 'pages/settings/pages/integrations/route/type'
import styles from './styles.module.scss'

const tabs: ITabItem[] = [
  {
    name: 'Settings',
    key: 'settings',
  },
  {
    name: 'Description',
    key: 'description',
  },
]

const IntegrationDetails = observer(() => {
  const { pathname } = useLocation()
  const {
    integration,
    isLoading,
    isNotFound,
    onConnect,
    onDisconnect,
    isManageConnectionProcessing,
    hasConnectionLimit,
  } = useIntegrationDetailsContext()
  const { featureFlagsStore, organizationStore } = useEntitiesContext()
  const { salesmsg_design_v2_salesforce } = featureFlagsStore

  if (isLoading) return <SpinnerLoader ariaLabel={'IntegrationDetailsLayout'} />
  if (isNotFound) {
    return <Navigate to={`/${SettingsRoutes.root}/${IntegrationsRoutes.root}`} replace />
  }
  if (!integration) return null

  if (integration.key === 'calendly' && !featureFlagsStore.salesmsg_design_v2_calendly_page) {
    return <Navigate to={`/${SettingsRoutes.root}/${IntegrationsRoutes.root}`} replace />
  }
  if (integration.key === 'zoho' && !featureFlagsStore.zoho_integration) {
    return <Navigate to={`/${SettingsRoutes.root}/${IntegrationsRoutes.root}`} replace />
  }

  const isHipaaCompliant = organizationStore.isHipaaCompliant
  const isIntegrationForbidden = isHipaaCompliant && integration.key !== 'hubspot'
  const segments = pathname.split('/').filter(Boolean)
  const activeTab = segments[segments.length - 1]
  const handleSelectTab = (tab: ITabItem) => {
    uiStore.changeRouteViaVue({
      path: `/${SettingsRoutes.root}/${IntegrationsRoutes.root}/${integration.key}/${tab.key}`,
    })
  }
  const integrationData = getIntegrationDetailsDataByKey(integration.key)
  const showConnectButton =
    integration.key === 'salesforce' && salesmsg_design_v2_salesforce
      ? false
      : !integration.isConnected

  const handleConnect = () => {
    void onConnect()
  }

  const getConnectButton = () => {
    if (integration.isNangoProvider) {
      return (
        <NangoConnectButton
          integration={integration}
          isIntegrationForbidden={isIntegrationForbidden}
        />
      )
    }
    if (integration.is_ampersand) {
      return (
        <AmpersandConnectButton
          integration={integration}
          isIntegrationForbidden={isIntegrationForbidden}
        />
      )
    }
    const isPackage = integration.key === 'agentforce'
    return (
      <Button
        disabled={hasConnectionLimit || isIntegrationForbidden || isPackage}
        text={isPackage ? 'Package' : 'Connect'}
        onClick={handleConnect}
        loading={isManageConnectionProcessing}
        tooltipProps={{
          visibility: isIntegrationForbidden ? 'visible' : 'hidden',
          label:
            'Disabled for HIPAA compliance. Contact Support if you need help or have questions about this integration.',
          placement: 'top',
          maxWidth: '356px',
          breakWord: true,
        }}
        iconProps={{ icon: 'plug' }}
        size='medium'
      />
    )
  }

  return (
    <FullPageWrapper className={styles.container}>
      <div className={styles.content}>
        <Breadcrumbs customPath={`/${SettingsRoutes.root}/${IntegrationsRoutes.root}`} />
        <header>
          <Icon
            fontSize={24}
            icon={integration.icon}
            padding={11}
            insideBox
            className={styles.headerLogo}
          />
          <div className={styles.integrationDetailsHeader}>
            <div className={styles.title}>
              <Typography variant='heading-lg' ariaLabel='IntegrationDetails_title'>
                {integration.name}
              </Typography>
              <IntegrationStatus integration={integration} />
            </div>
            {integration.url || integration.user_credential?.meta.hub_domain ? (
              <Tooltip
                type='description'
                label={integration.url}
                desc={integration.user_credential?.meta.hub_domain}
                placement='top'
              >
                <Typography
                  variant='body-md-regular'
                  ariaLabel='IntegrationDetails_desc'
                  color='var(--content-primary-tertiary)'
                  ellipsis
                  tag='div'
                  width='421px'
                >
                  Environment: &nbsp;
                  {[integration.url, integration.user_credential?.meta.hub_domain]
                    .filter(Boolean)
                    .join(' - ')}
                </Typography>
              </Tooltip>
            ) : null}
          </div>
          <div className={styles.actions}>
            {integrationData?.docs && (
              <Button
                tag='a'
                text='Docs'
                size='medium'
                contained='secondary'
                iconProps={{ icon: 'link' }}
                href={integrationData.docs}
                target='_blank'
                rel='noreferrer'
                className={styles.docsButton}
              />
            )}
            {integration.isConnected && (
              <Button
                text='Disconnect'
                onClick={onDisconnect}
                loading={isManageConnectionProcessing}
                typeBtn='outlined'
                contained='tertiary'
                iconProps={{ icon: 'unlink' }}
                size='medium'
              />
            )}
            {showConnectButton && getConnectButton()}
          </div>
        </header>
        {integration.isSettingsAvailable && (
          <div className={styles.navBar}>
            <Tabs
              variant='default'
              tabs={tabs}
              activeTabKey={activeTab}
              handleSelectTab={handleSelectTab}
            ></Tabs>
          </div>
        )}
        <Outlet />
      </div>
    </FullPageWrapper>
  )
})

export const IntegrationDetailsLayout = () => (
  <IntegrationDetailsProvider>
    <IntegrationDetails />
  </IntegrationDetailsProvider>
)
